import React, { useState, useContext } from 'react';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import useSWR from 'swr';

import { AppContext } from 'context/AppContext';
import { useEnrollment } from '../../../services/EnrollmentService';
import TaskList from './TaskList';
import RankingsList from './RankingsList';
import Teams from './Teams';
import OrganizerBox from './OrganizerBox';
import RelatedCampaigns from './RelatedCampaigns';
import { Campaign } from 'models/CommonType';
import { truncateString, urlify } from 'utils/helpers';

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 32px;
    
    &::before {
      border-bottom: 2px solid var(--color-border-light);
    }
  }

  /* Reduce navigation spacing on mobile */
  @media (max-width: 768px) {
    .ant-tabs-nav {
      margin-bottom: 8px;
    }
  }
  
  .ant-tabs-tab {
    padding: 16px 24px;
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-tertiary);
    transition: all 0.3s ease;
    
    &:hover {
      color: var(--color-primary);
    }
    
    &.ant-tabs-tab-active {
      .ant-tabs-tab-btn {
        color: var(--color-primary);
        font-weight: 700;
      }
    }
  }
  
  .ant-tabs-ink-bar {
    background: linear-gradient(90deg, var(--color-primary), var(--color-primary-dark));
    height: 3px;
    border-radius: 2px;
  }
  
  .ant-tabs-content-holder {
    padding-top: 24px;
  }
`;

interface CampaignTabsProps {
  campaign: Campaign;
  tasks: any[];
  authKey: string;
  isMobile?: boolean;
  taskSortBy: string;
  onTaskSortChange: (sortBy: string) => void;
}

const CampaignTabs: React.FC<CampaignTabsProps> = ({
  campaign,
  tasks,
  authKey,
  isMobile = false,
  taskSortBy,
  onTaskSortChange,
}) => {
  const { t } = useTranslation();
  const { api } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState('tasks');

  const { data: userDetailsResponse, isLoading: userLoading } = useSWR(
    authKey ? 'user-details' : null,
    () => api.profile.getUserDetails(),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const currentUser = userDetailsResponse?.error
    ? null
    : (userDetailsResponse?.results?.[0] || userDetailsResponse) as any;

  const { data: userEnrollment, isLoading: enrollmentLoading } = useEnrollment(
    campaign.id,
    currentUser?.id || '',
    authKey
  );

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const tabItems = [
    {
      key: 'tasks',
      label: t('#Campaign.tasks'),
      children: (
        <div>
          {/* Teams section for both mobile and web */}
          {campaign.is_enable_team && campaign.is_joined && (
            <div style={{ marginBottom: '32px' }}>
              <Teams
                campaign={campaign}
                authKey={authKey}
                hasTeam={userEnrollment?.has_team || false}
                isJoined={campaign.is_joined}
                enrollment={userEnrollment || undefined}
              />
            </div>
          )}
          
          {/* Tasks list with sorting */}
          <TaskList 
            tasks={tasks || []} 
            sortBy={taskSortBy}
            onSortChange={onTaskSortChange}
            hideHeading={!(campaign.is_enable_team && campaign.is_joined)}
          />
        </div>
      )
    },
  ];

  // Details tab (mobile) or dedicated tab (web)
  if (isMobile) {
    tabItems.push({
      key: 'details',
      label: t('#Campaign.details'),
      children: (
        <div>
          {/* Description */}
          <div style={{ marginBottom: '24px' }}>
            <div className="CampaignPageLabel">{t('#Campaign.description')}</div>
            <div
              className="CampaignPageDescription"
              dangerouslySetInnerHTML={{ __html: urlify(campaign.description) }}
            />
          </div>
          
          {/* Organizer or Organizer Team */}
          <div style={{ marginBottom: '24px' }}>
            {campaign.users && campaign.users.length > 0 ? (
              <>
                <div className="CampaignPageLabel">
                  {t('#Campaign.organizer_team')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {(() => {
                    // Create a unique list ensuring campaign.user is included and no duplicates
                    const allOrganizers = [];
                    const seenIds = new Set();
                    
                    // Always include the main organizer first
                    if (campaign.user && campaign.user.id && !seenIds.has(campaign.user.id)) {
                      allOrganizers.push(campaign.user);
                      seenIds.add(campaign.user.id);
                    }
                    
                    // Add users from the team, avoiding duplicates
                    campaign.users.forEach(user => {
                      if (user && user.id && !seenIds.has(user.id)) {
                        allOrganizers.push(user);
                        seenIds.add(user.id);
                      }
                    });
                    
                    return allOrganizers.map((user, index) => (
                      <OrganizerBox
                        key={user.id || index}
                        avatar={user.profile?.image_small_url}
                        user={user}
                        bio={truncateString(user.profile?.bio ?? '', 200)}
                      />
                    ));
                  })()
                  }
                </div>
              </>
            ) : (
              <>
                <div className="CampaignPageLabel">
                  {t('#Campaign.organizer')}
                </div>
                <OrganizerBox
                  avatar={campaign.user?.profile?.image_small_url}
                  user={campaign.user}
                  bio={truncateString(campaign.user?.profile?.bio ?? '', 200)}
                />
              </>
            )}
          </div>
          
          {/* Related campaigns */}
          <div>
            <div className="CampaignPageLabel">{t('#Campaign.related_campaigns')}</div>
            {campaign.user?.id && (
              <RelatedCampaigns
                userId={campaign.user.id}
                authKey={authKey}
                currentCampaignId={campaign.id}
                isMobile={isMobile}
              />
            )}
          </div>
        </div>
      )
    });
  } else {
    // Web version - show related campaigns only
    tabItems.push({
      key: 'details',
      label: t('#Campaign.related_campaigns'),
      children: (
        <div>
          {campaign.user?.id ? (
            <RelatedCampaigns
              userId={campaign.user.id}
              authKey={authKey}
              currentCampaignId={campaign.id}
              isMobile={isMobile}
            />
          ) : (
            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
              {t('#Campaign.no_related_campaigns')}
            </div>
          )}
        </div>
      )
    });
  }

  // Progress tab (was rankings)
  if (userEnrollment && !enrollmentLoading && !userLoading) {
    tabItems.push({
      key: 'progress',
      label: t('#Campaign.progress'),
      children: (
        <RankingsList
          campaign={campaign}
          authKey={authKey}
        />
      )
    });
  }

  return (
    <StyledTabs
      defaultActiveKey="tasks"
      activeKey={activeTab}
      onChange={handleTabChange}
      size="large"
      items={tabItems}
    />
  );
};

export default CampaignTabs;